import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiEye, FiHome, FiPaperclip, FiSearch, FiX } from 'react-icons/fi';
import { API_URL, BASE_URL } from '../../Api/api';
import { getAuthToken, getAuthUser } from '../../Api/authToken';
import prepareChatFile, { isPdfAttachment } from '../../utils/prepareChatFile';
import './ChatInbox.css';

const headers = () => ({ Authorization: `Bearer ${getAuthToken()}` });

const mediaUrl = (path) => {
  if (!path) return '';
  const value = String(path);
  if (value.startsWith('http') || value.startsWith('blob:') || value.startsWith('data:')) return value;
  return `${BASE_URL}/${value.replace(/^\/+/, '')}`;
};

const formatPrice = (value) => {
  if (value == null || value === '') return '';
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return String(value);
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
};

const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path d="M22 2L11 13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 2l-7 20-4-9-9-4 20-7z" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StatusMark = ({ status }) => {
  if (!status || status === 'pending') {
    return <span className="msg-status" aria-label="Sending"><i className="msg-dot" /></span>;
  }
  const seen = status === 'seen';
  const delivered = status === 'delivered' || seen;
  return (
    <span className={`msg-status${seen ? ' is-seen' : ''}`} aria-label={seen ? 'Seen' : delivered ? 'Delivered' : 'Sent'}>
      <svg viewBox="0 0 18 12" width="16" height="11" aria-hidden="true">
        <path d="M1.2 6.2 L4.4 9.4 L10.2 2.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        {delivered && <path d="M6.2 6.2 L9.4 9.4 L16.2 2.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />}
      </svg>
    </span>
  );
};

const formatTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ChatInbox = () => {
  const { pathname } = useLocation();
  const guestMode = pathname.includes('/unknown-user');
  const [params, setParams] = useSearchParams();
  const selectedId = Number(params.get(guestMode ? 'thread' : 'user')) || null;
  const [threads, setThreads] = useState([]);
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [propertyOpen, setPropertyOpen] = useState(false);
  const [draftProperty, setDraftProperty] = useState(null);
  const [propertyQuery, setPropertyQuery] = useState('');
  const [propertyResults, setPropertyResults] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [zoom, setZoom] = useState('');
  const [dragging, setDragging] = useState(false);
  const [denied, setDenied] = useState(false);
  const canEditReplies = getAuthUser()?.is_super === 1;
  const photoRef = useRef(null);
  const stickToBottom = useRef(true);
  const listRef = useRef(null);
  const typingSent = useRef(0);

  const loadThreads = useCallback(async () => {
    try {
      const res = await axios.get(guestMode ? `${API_URL}/admin/chat/guests` : `${API_URL}/admin/chat/users`, { headers: headers() });
      const rows = guestMode ? res.data?.guests : res.data?.users;
      setThreads(Array.isArray(rows) ? rows : []);
      setDenied(false);
    } catch (error) {
      if (error.response?.status === 403) setDenied(true);
      /* keep the last list if a refresh fails */
    }
  }, [guestMode]);

  const loadMessages = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await axios.get(
        guestMode ? `${API_URL}/admin/chat/threads/${id}/messages` : `${API_URL}/admin/chat/users/${id}/messages`,
        { headers: headers() },
      );
      const thread = res.data?.thread || null;
      setActive(thread);
      if (thread?.id) {
        axios.post(`${API_URL}/admin/chat/threads/${thread.id}/read`, {}, { headers: headers() }).catch(() => {});
      }
      const incoming = Array.isArray(res.data?.messages) ? res.data.messages : [];
      setMessages((prev) => incoming.map((item) => {
        if (item.property_id) return item;
        const older = prev.find((row) => row.id === item.id);
        if (!older?.property_id) return item;
        return {
          ...item,
          property_id: older.property_id,
          listing_id: older.listing_id,
          property_title: older.property_title,
          property_location: older.property_location,
          property_image: older.property_image,
          property_price: older.property_price,
        };
      }));
    } catch {
      /* ignore a single failed poll */
    }
  }, [guestMode]);

  useEffect(() => {
    loadThreads();
    const timer = setInterval(loadThreads, 4000);
    return () => clearInterval(timer);
  }, [loadThreads]);

  useEffect(() => {
    if (!selectedId) {
      setActive(null);
      setMessages([]);
      return undefined;
    }
    const refresh = () => {
      loadMessages(selectedId);
    };
    refresh();
    const timer = setInterval(refresh, 1000);
    return () => clearInterval(timer);
  }, [selectedId, loadMessages]);

  useEffect(() => {
    stickToBottom.current = true;
  }, [selectedId]);

  useEffect(() => {
    const node = listRef.current;
    if (!node || !stickToBottom.current) return undefined;
    const pin = () => { node.scrollTop = node.scrollHeight; };
    pin();
    const frame = requestAnimationFrame(pin);
    return () => cancelAnimationFrame(frame);
  }, [messages, active?.user_typing, selectedId]);

  const onLogScroll = () => {
    const node = listRef.current;
    if (!node) return;
    stickToBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < 72;
  };

  useEffect(() => {
    if (!propertyOpen) return undefined;
    const q = propertyQuery.trim();
    if (!q) {
      setPropertyResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/chat/properties`, { params: { q }, headers: headers() });
        setPropertyResults(Array.isArray(res.data?.properties) ? res.data.properties : []);
      } catch {
        setPropertyResults([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [propertyQuery, propertyOpen]);

  const chooseProperty = (item) => {
    setDraftProperty(item);
    setPropertyOpen(false);
    setPropertyQuery('');
  };

  const openThread = (id) => {
    setDraftProperty(null);
    setPropertyOpen(false);
    setEditingId(null);
    setText('');
    setPhoto((current) => {
      if (current?.url) URL.revokeObjectURL(current.url);
      return null;
    });
    setParams(guestMode ? { thread: String(id) } : { user: String(id) });
  };

  const clearPhoto = () => {
    setPhoto((current) => {
      if (current?.url) URL.revokeObjectURL(current.url);
      return null;
    });
  };

  const attachChatFile = async (file) => {
    if (!file) return;
    try {
      const next = await prepareChatFile(file);
      setPhoto((current) => {
        if (current?.url) URL.revokeObjectURL(current.url);
        return next;
      });
    } catch {
      /* keep the composer usable */
    }
  };

  const pickPhoto = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    attachChatFile(file);
  };

  const onDropFile = (event) => {
    event.preventDefault();
    setDragging(false);
    attachChatFile(event.dataTransfer.files?.[0]);
  };

  const beginEdit = (message) => {
    clearPhoto();
    setEditingId(message.id);
    setText(message.body || '');
  };

  const send = async (event) => {
    event.preventDefault();
    const body = text.trim();
    const draft = draftProperty;
    const picture = photo;
    const threadId = active?.id;
    if (!threadId || sending) return;
    if (editingId) {
      if (!body) return;
      setSending(true);
      try {
        const res = await axios.patch(
          `${API_URL}/admin/chat/threads/${threadId}/messages/${editingId}`,
          { text: body },
          { headers: headers() },
        );
        if (Array.isArray(res.data?.messages)) setMessages(res.data.messages);
        if (res.data?.thread) setActive(res.data.thread);
        setEditingId(null);
        setText('');
        loadThreads();
      } finally {
        setSending(false);
      }
      return;
    }
    if (!body && !draft && !picture) return;
    stickToBottom.current = true;
    setSending(true);
    setText('');
    setDraftProperty(null);
    setPhoto(null);
    try {
      const form = new FormData();
      form.append('text', body);
      if (draft?.property_id) form.append('property_id', String(draft.property_id));
      if (picture?.file) form.append('image', picture.file);
      const res = await axios.post(
        `${API_URL}/admin/chat/threads/${threadId}/messages`,
        form,
        { headers: headers() },
      );
      if (picture?.url) URL.revokeObjectURL(picture.url);
      if (Array.isArray(res?.data?.messages)) {
        const rows = res.data.messages.map((item) => ({ ...item }));
        if (draft) {
          const match = [...rows].reverse().find((item) => item.sender === 'admin' && item.body === (body || draft.title));
          if (match && !match.property_id) {
            match.property_id = draft.property_id;
            match.listing_id = draft.id;
            match.property_title = draft.title;
            match.property_location = draft.area || '';
            match.property_image = draft.image || '';
            match.property_price = draft.price || '';
          }
        }
        setMessages(rows);
      }
      else if (res?.data?.message) setMessages((prev) => [...prev, res.data.message]);
      if (res?.data?.thread) setActive(res.data.thread);
      typingSent.current = 0;
      loadThreads();
    } catch {
      setText(body);
      setDraftProperty(draft);
      setPhoto(picture);
    } finally {
      setSending(false);
    }
  };

  const onType = (value) => {
    setText(value);
    const threadId = active?.id;
    if (!threadId) return;
    const now = Date.now();
    const activeTyping = value.trim().length > 0;
    if (activeTyping && now - typingSent.current < 2000) return;
    if (!activeTyping && typingSent.current === 0) return;
    typingSent.current = activeTyping ? now : 0;
    axios.post(
      `${API_URL}/admin/chat/threads/${threadId}/typing`,
      { typing: activeTyping },
      { headers: headers() },
    ).catch(() => {});
  };

  const needle = query.trim().toLowerCase();
  const visibleThreads = (needle
    ? threads.filter((thread) => [thread.name, thread.email, thread.phone, thread.last_message]
      .some((value) => String(value || '').toLowerCase().includes(needle)))
    : threads
  ).slice().sort((a, b) => {
    const aAt = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
    const bAt = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
    if (bAt !== aAt) return bAt - aAt;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });

  if (denied) {
    return (
      <div className="admin-page chat-inbox-page">
        <div className="page-header">
          <h1>{guestMode ? 'Unknown User' : 'User Chat'}</h1>
          <p>User Chat is not enabled for this admin account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page chat-inbox-page">
      <div className="page-header">
        <h1>{guestMode ? 'Unknown User' : 'User Chat'}</h1>
        <p>{guestMode ? 'Messages from visitors who are not signed in.' : 'Every user is listed here. A user can reply when Access Chat is on.'}</p>
      </div>

      <div className={`chat-inbox${selectedId ? ' thread-open' : ''}`}>
        <aside className="chat-thread-list">
          <div className="chat-list-search">
            <FiSearch aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={guestMode ? 'Search name, email, or phone' : 'Search users'}
              aria-label={guestMode ? 'Search unknown users' : 'Search users'}
            />
          </div>
          {threads.length === 0 && <p className="chat-empty">{guestMode ? 'No unknown user messages yet.' : 'No users found.'}</p>}
          {threads.length > 0 && visibleThreads.length === 0 && <p className="chat-empty">{guestMode ? 'No messages match that search.' : 'No users match that search.'}</p>}
          {visibleThreads.map((thread) => (
            <button
              type="button"
              key={thread.id}
              className={`chat-thread${thread.id === selectedId ? ' is-active' : ''}`}
              onClick={() => openThread(thread.id)}
            >
              <span className="chat-thread__top">
                <strong>{thread.name || 'User'}</strong>
                {thread.admin_unread > 0 && <em>{thread.admin_unread}</em>}
              </span>
              <span className="chat-thread__preview">{thread.last_message || (thread.chat_access ? 'No message yet' : 'Chat access off')}</span>
              {thread.last_staff_name ? <span className="chat-thread__staff">Last: {thread.last_staff_name}</span> : null}
              <span className="chat-thread__time">{formatTime(thread.last_message_at)}</span>
            </button>
          ))}
        </aside>

        <section
          className={`chat-pane${dragging ? ' is-dropping' : ''}`}
          onDragOver={(event) => { event.preventDefault(); if (selectedId) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDropFile}
        >
          {!selectedId && <p className="chat-empty">{guestMode ? 'Select a visitor to reply.' : 'Select a user to start chatting.'}</p>}
          {selectedId && (
            <>
              <header className="chat-pane__head">
                <button type="button" className="chat-back" onClick={() => setParams({})} aria-label="Back to chats">
                  <FiArrowLeft />
                </button>
                <div>
                  <strong>{active?.name || threads.find((user) => user.id === selectedId)?.name || 'User'}</strong>
                  <p>
                    {[active?.email, active?.phone].filter(Boolean).join(' · ') || 'Contact details unavailable'}
                    {threads.find((user) => user.id === selectedId)?.chat_access === false ? ' · Chat access is off, so this user cannot reply yet.' : ''}
                  </p>
                  {active?.property_id && (
                    <p className="chat-pane__property">
                      Property #{active.property_id}{active.property_title ? ` · ${active.property_title}` : ''}
                    </p>
                  )}
                </div>
              </header>
              <div className="chat-pane__log" ref={listRef} onScroll={onLogScroll}>
                {messages.filter((message) => {
                  const started = messages.some((item) => item.sender === 'user' || item.sender === 'admin');
                  const greeting = message.kind === 'greeting' || (message.sender === 'system' && /^hello! how can we help you today\??$/i.test(message.body || ''));
                  return !(started && greeting);
                }).map((message) => {
                  const mine = message.sender === 'admin';
                  const manageable = canEditReplies && mine && !message.deleted && message.kind !== 'greeting' && message.kind !== 'wait';
                  return (
                    <div key={message.id} className={`chat-msg-row chat-msg-row--${message.sender}`}>
                      <div className={`chat-msg chat-msg--${message.sender}${message.property_id ? ' has-property' : ''}`}>
                        {message.deleted ? (
                          <span className="msg-deleted">This message was deleted</span>
                        ) : (
                          <>
                            {message.sender === 'admin' && message.sender_name ? <span className="chat-msg__who">{message.sender_name}</span> : null}
                            {isPdfAttachment(message) ? (
                              message.image ? (
                                <a className="chat-pdf" href={mediaUrl(message.image)} target="_blank" rel="noreferrer">{message.file_name || 'PDF'}</a>
                              ) : (
                                <span className="chat-pdf">{message.file_name || 'PDF'}</span>
                              )
                            ) : message.image ? (
                              <button type="button" className="chat-photo-btn" onClick={() => setZoom(mediaUrl(message.image))}>
                                <img src={mediaUrl(message.image)} alt="" />
                              </button>
                            ) : null}
                            {message.property_id && (
                              <article className="chat-prop-card">
                                {message.property_image ? <img src={mediaUrl(message.property_image)} alt="" /> : <div className="chat-prop-card__photo" />}
                                <div>
                                  <strong>#{message.property_id}</strong>
                                  <span>{message.property_title || message.body}</span>
                                  <em>{[message.property_location, formatPrice(message.property_price)].filter(Boolean).join(' · ')}</em>
                                </div>
                                <a href={`/property/${message.listing_id || message.property_id}`} target="_blank" rel="noreferrer" aria-label="View property">
                                  <FiEye />
                                </a>
                              </article>
                            )}
                            {message.kind !== 'property' && message.body ? <span>{message.body}</span> : null}
                            {message.edited_at && <small className="msg-edited">Edited</small>}
                          </>
                        )}
                        {mine && !message.deleted && <StatusMark status={message.status || 'sent'} />}
                      </div>
                      {manageable && message.kind !== 'property' && (
                        <div className="msg-actions">
                          <button type="button" onClick={() => beginEdit(message)}>Edit</button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {active?.user_typing && <div className="chat-typing">User is typing…</div>}
              </div>
              {propertyOpen && (
                <div className="chat-property-picker">
                  <input
                    value={propertyQuery}
                    onChange={(e) => setPropertyQuery(e.target.value)}
                    placeholder="Search by property ID or area"
                    aria-label="Search properties"
                  />
                  <div>
                    {propertyResults.map((item) => (
                      <button type="button" key={`${item.id}-${item.property_id}`} onClick={() => chooseProperty(item)}>
                        <strong>#{item.property_id}</strong>
                        <span>{item.title}{item.area ? ` · ${item.area}` : ''}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {draftProperty && (
                <div className="chat-prop-draft">
                  <div>
                    <strong>#{draftProperty.property_id}</strong>
                    <span>{draftProperty.title}</span>
                  </div>
                  <button type="button" onClick={() => setDraftProperty(null)} aria-label="Remove selected property">
                    <FiX />
                  </button>
                </div>
              )}
              {photo?.kind === 'pdf' && (
                <div className="chat-photo-draft chat-file-draft">
                  <span>{photo.name}</span>
                  <button type="button" onClick={clearPhoto} aria-label="Remove PDF">
                    <FiX />
                  </button>
                </div>
              )}
              {photo?.kind === 'image' && (
                <div className="chat-photo-draft">
                  <img src={photo.url} alt="" />
                  <button type="button" onClick={clearPhoto} aria-label="Remove photo">
                    <FiX />
                  </button>
                </div>
              )}
              {dragging && <p className="chat-drop-hint">Drop a photo or PDF to attach it</p>}
              {editingId && (
                <div className="chat-editing">
                  <span>Editing reply</span>
                  <button type="button" onClick={() => { setEditingId(null); setText(''); }}>Cancel</button>
                </div>
              )}
              <form className="chat-pane__compose" onSubmit={send}>
                <button type="button" className={`chat-tool chat-tool--property${propertyOpen ? ' is-on' : ''}`} onClick={() => setPropertyOpen((value) => !value)}>
                  <FiHome aria-hidden="true" />
                  <span>Property</span>
                </button>
                <button type="button" className="chat-tool chat-tool--file" aria-label="Attach a file" onClick={() => photoRef.current?.click()} disabled={!!editingId}>
                  <FiPaperclip aria-hidden="true" />
                </button>
                <input ref={photoRef} type="file" accept="image/*,application/pdf,.pdf" hidden onChange={pickPhoto} />
                <input
                  value={text}
                  onChange={(e) => onType(e.target.value)}
                  placeholder={editingId ? 'Edit your reply' : (draftProperty || photo ? 'Add a reply, or just send' : 'Write a reply')}
                  aria-label="Reply"
                />
                <button type="submit" disabled={sending || (!text.trim() && !draftProperty && !photo)} aria-label={editingId ? 'Save edit' : 'Send reply'}>
                  <SendIcon />
                </button>
              </form>
            </>
          )}
        </section>
      </div>
      {zoom && (
        <button type="button" className="chat-zoom" onClick={() => setZoom('')} aria-label="Close photo">
          <img src={zoom} alt="" />
        </button>
      )}
    </div>
  );
};

export default ChatInbox;
